/**
 * RBAC catalog — single source of truth for permission keys and the default permission set
 * of each system role. The database `permissions` table is synced from here after migrations.
 */
export const PERMISSIONS = {
  // services
  'services.view': 'Ver servicios',
  'services.create': 'Crear servicios',
  'services.edit': 'Editar servicios',
  'services.cancel': 'Cancelar servicios',
  'services.assign': 'Asignar servicios manualmente',
  'services.dispatch': 'Operar el despacho (re-despachar, cerrar)',
  // drivers
  'drivers.view': 'Ver conductores',
  'drivers.create': 'Crear conductores',
  'drivers.edit': 'Editar conductores',
  'drivers.block': 'Suspender / reactivar conductores',
  'drivers.locate': 'Ver ubicación de conductores',
  // vehicles & documents
  'vehicles.view': 'Ver vehículos',
  'vehicles.create': 'Crear vehículos',
  'vehicles.edit': 'Editar vehículos',
  'documents.view': 'Ver documentos',
  'documents.review': 'Aprobar / rechazar documentos',
  'documents.configure': 'Configurar los tipos de documento que se exigen',
  // customers
  'customers.view': 'Ver clientes',
  'customers.edit': 'Editar clientes',
  'customers.anonymize': 'Anonimizar clientes',
  // driver access / quotas
  'quotas.view': 'Ver cuotas de conductores',
  'quotas.manage': 'Gestionar los planes y las cuotas de los conductores',
  // the suspension for unpaid quotas is automatic (policy); this is the manual one and the policy itself
  'quotas.enforce': 'Suspender o reactivar a mano la cuota de un conductor y fijar la política de mora',
  'payments.register': 'Registrar pagos externos',
  'payments.void': 'Anular pagos registrados',
  // loyalty
  'loyalty.view': 'Ver fidelización',
  'loyalty.manage': 'Configurar fidelización (programas, reglas, niveles, beneficios)',
  'loyalty.campaigns': 'Gestionar campañas y segmentos de fidelización',
  'loyalty.adjust': 'Ajustar y revertir puntos de clientes',
  'loyalty.redeem': 'Canjear y entregar beneficios a clientes',
  'loyalty.analytics': 'Ver analítica de fidelización',
  // pricing / dispatch configuration
  'pricing.view': 'Ver tarifas',
  'pricing.manage': 'Configurar tarifas',
  'dispatch.configure': 'Configurar reglas de despacho',
  // reports / billing
  'reports.view': 'Ver reportes',
  'reports.export': 'Exportar reportes',
  'finance.view': 'Ver los ingresos de los viajes (finanzas de Indicadores)',
  'billing.view': 'Ver la suscripción: el plan y los cobros de la plataforma',
  // administration
  'users.view': 'Ver usuarios',
  'users.manage': 'Gestionar usuarios',
  'users.reset_access': 'Restablecer el acceso de usuarios (enlaces, códigos, verificación en 2 pasos, sesiones)',
  'roles.manage': 'Gestionar roles y permisos',
  'settings.manage': 'Configuración del tenant',
  'security.manage': 'Gestionar la política de seguridad (2FA obligatoria)',
  'security.view_events': 'Ver el historial de seguridad (ingresos, sesiones y cambios de acceso)',
  'branding.manage': 'Gestionar marca',
  'integrations.manage': 'Gestionar integraciones (WhatsApp, mapas)',
  'audit.view': 'Ver auditoría',
  'locations.view': 'Ver analítica de ubicaciones (búsquedas sin resolver)',
  'locations.manage': 'Gestionar lugares, alias y correcciones de ubicaciones',
  'locations.import': 'Importar lugares desde archivos',
  'locations.export': 'Exportar el catálogo de lugares',
  'dashboard.view': 'Ver dashboard operativo',
  'voice.call': 'Llamadas de voz con conductores',
  // push-to-talk radio (docs/ptt.md)
  'communication.view': 'Ver canales de radio',
  'communication.listen': 'Escuchar canales de radio',
  'communication.transmit': 'Transmitir por radio (pulsar para hablar)',
  'communication.manage_channels': 'Crear, editar y archivar canales de radio',
  'communication.manage_members': 'Gestionar miembros de canales de radio',
  'communication.moderate': 'Moderar la radio (silenciar, bloquear, expulsar, cortar transmisiones)',
  'communication.view_history': 'Ver historial de transmisiones de radio',
  // silent panic button of the driver (PNC-001)
  'panic.view': 'Ver alertas de pánico de los conductores',
  'panic.manage': 'Atender y cerrar alertas de pánico',
  'panic.whatsapp': 'Recibir por WhatsApp las alertas de pánico (además del panel)',
  // driver app
  'driver.app': 'Usar la aplicación de conductor',
  'offers.receive': 'Recibir ofertas de servicio',
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;
export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as PermissionKey[];

export function permissionModule(key: PermissionKey): string {
  return key.split('.')[0]!;
}

export const SYSTEM_ROLES = [
  'TENANT_OWNER',
  'ADMIN',
  'DISPATCHER',
  'SUPERVISOR',
  'CALL_CENTER',
  'ACCOUNTING',
  'FLEET_MANAGER',
  'DRIVER',
] as const;
export type SystemRole = (typeof SYSTEM_ROLES)[number];

/** Permissions of the driver app: staff roles never hold them. */
export const DRIVER_APP_PERMISSIONS = ['driver.app', 'offers.receive'] as const satisfies readonly PermissionKey[];

/**
 * Permissions that only decide what reaches a person (a notification channel), never what the person
 * can see or do in the tenant: handing them out is no escalation (can-manage.ts `missingGrants`).
 */
export const NOTIFY_ONLY_PERMISSIONS = ['panic.whatsapp'] as const satisfies readonly PermissionKey[];

const ALL_STAFF = PERMISSION_KEYS.filter((k) => !(DRIVER_APP_PERMISSIONS as readonly PermissionKey[]).includes(k));

/**
 * What only the owner holds among the staff permissions (RBAC-002, docs/audits/2026-10-31-architecture-rbac.md):
 * who may do what (roles, security policy), the contract with the platform (subscription), the
 * irreversible erasure of a customer (Ley 1581) and the panic alerts by WhatsApp, which cost a
 * message per person (the owner hands it to whoever else should get them).
 */
const OWNER_ONLY: readonly PermissionKey[] = ['roles.manage', 'security.manage', 'billing.view', 'customers.anonymize', 'panic.whatsapp'];

/**
 * Default roles of every tenant. Passengers are not users: they are `customers` reached by WhatsApp
 * (the CUSTOMER role, never used, was retired in RBAC-002).
 */
export const SYSTEM_ROLE_DEFINITIONS: Record<SystemRole, { name: string; permissions: PermissionKey[] }> = {
  TENANT_OWNER: { name: 'Propietario de la empresa', permissions: ALL_STAFF },
  ADMIN: {
    name: 'Administrador',
    permissions: ALL_STAFF.filter((k) => !OWNER_ONLY.includes(k)),
  },
  DISPATCHER: {
    name: 'Despachador',
    permissions: [
      'dashboard.view', 'services.view', 'services.create', 'services.edit', 'services.cancel',
      'services.assign', 'services.dispatch', 'drivers.view', 'drivers.locate', 'customers.view',
      'vehicles.view', 'voice.call', 'pricing.view',
      'communication.view', 'communication.listen', 'communication.transmit', 'communication.moderate',
      'panic.view', 'panic.manage',
    ],
  },
  SUPERVISOR: {
    name: 'Supervisor',
    permissions: [
      'dashboard.view', 'services.view', 'services.create', 'services.edit', 'services.cancel', 'services.assign',
      'services.dispatch', 'drivers.view', 'drivers.edit', 'drivers.block', 'drivers.locate',
      'customers.view', 'vehicles.view', 'documents.view', 'reports.view', 'audit.view', 'locations.view', 'voice.call',
      'quotas.view', 'loyalty.view', 'loyalty.analytics',
      'communication.view', 'communication.listen', 'communication.transmit', 'communication.moderate',
      'communication.manage_members', 'communication.view_history',
      'panic.view', 'panic.manage', 'panic.whatsapp',
    ],
  },
  CALL_CENTER: {
    name: 'Call center',
    permissions: ['dashboard.view', 'services.view', 'services.create', 'services.cancel', 'customers.view', 'customers.edit', 'pricing.view', 'loyalty.view'],
  },
  ACCOUNTING: {
    name: 'Contabilidad',
    // money without operating: plans and payments yes; suspending a driver by hand (quotas.enforce) no
    permissions: ['quotas.view', 'quotas.manage', 'payments.register', 'payments.void', 'reports.view', 'reports.export', 'billing.view', 'finance.view', 'drivers.view'],
  },
  FLEET_MANAGER: {
    name: 'Flota',
    permissions: [
      'dashboard.view', 'drivers.view', 'drivers.create', 'drivers.edit', 'vehicles.view', 'vehicles.create',
      'vehicles.edit', 'documents.view', 'documents.review', 'documents.configure', 'quotas.view', 'reports.view',
    ],
  },
  DRIVER: {
    name: 'Conductor',
    permissions: ['driver.app', 'offers.receive', 'communication.view', 'communication.listen', 'communication.transmit'],
  },
};

export function hasPermission(granted: ReadonlySet<string>, required: PermissionKey | PermissionKey[]): boolean {
  const list = Array.isArray(required) ? required : [required];
  return list.every((p) => granted.has(p));
}
